import { pageScrollBehavior } from "@/lib/scroll";
import { useEffect, useState } from "react";
import { ArrowRight, Gift, X } from "lucide-react";
import { useLocale } from "@/i18n/context";
import { trackCtaClick } from "@/lib/funnel";

const STORAGE_KEY = "nreport_announcement_dismissed";

/**
 * Site offer bar promoting the free preview report. Sticks
 * just under the fixed header so it stays visible while scrolling, and is
 * dismissible (persisted). Rendered visible by default so it's in the
 * prerendered HTML and doesn't cause layout shift for first-time visitors.
 */
export function AnnouncementBar() {
  const { t } = useLocale();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(STORAGE_KEY) === "1") setDismissed(true);
  }, []);

  if (dismissed) return null;

  const handleCta = () => {
    trackCtaClick("announcement_free_preview");
    const input = document.querySelector<HTMLInputElement>('#hero [role="combobox"]');
    input?.focus({ preventScroll: true });
    input?.scrollIntoView({ behavior: pageScrollBehavior(), block: "center" });
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, "1");
  };

  return (
    <div id="announcement-bar" className="sticky top-16 z-40 bg-[#E8EFD8] text-[#244D38] border-b border-[#D9E3CD]">
      <div className="container mx-auto flex items-center justify-center gap-2 px-10 py-2 text-sm">
        <Gift className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <button
          onClick={handleCta}
          className="inline-flex flex-wrap items-center justify-center gap-x-1.5 text-center font-medium hover:underline"
        >
          <span>{t.announcement.text}</span>
          <span className="inline-flex items-center gap-1 font-semibold underline underline-offset-2">
            {t.announcement.cta}
            <ArrowRight className="h-4 w-4" />
          </span>
        </button>
        <button
          onClick={handleDismiss}
          aria-label={t.announcement.dismiss}
          className="absolute right-3 rounded p-1 text-[#244D38] transition-colors hover:bg-black/5"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
